import type { CookieOptions } from "./cookie.js";
import { getCookie, setCookie } from "./cookie.js";

const LS_PREFIX = "ets_";

/**
 * Read a value from cookie, with localStorage fallback.
 * If localStorage has the value but cookie was cleared, re-persist to cookie.
 */
export function getPersistedValue(
  cookieName: string,
  cookieOptions: CookieOptions,
): string | undefined {
  const cookieValue = getCookie(cookieName);
  if (cookieValue) return cookieValue;

  // Fallback: check localStorage
  const lsValue = getLocalStorage(cookieName);
  if (lsValue) {
    // Re-persist to cookie
    setCookie(cookieName, lsValue, cookieOptions);
    return lsValue;
  }

  return undefined;
}

/**
 * Persist a value to both cookie and localStorage.
 */
export function setPersistedValue(
  cookieName: string,
  value: string,
  cookieOptions: CookieOptions,
): void {
  setCookie(cookieName, value, cookieOptions);
  setLocalStorage(cookieName, value);
}

/**
 * Read a JSON-structured value from localStorage (keyed by cookie name).
 * Returns undefined if missing, corrupt, or fails the guard check.
 */
export function getPersistedJson<T>(
  cookieName: string,
  guard: (value: unknown) => value is T,
): T | undefined {
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}${cookieName}`);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return guard(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Write a JSON-structured value to localStorage (keyed by cookie name).
 */
export function setPersistedJson<T>(cookieName: string, value: T): void {
  try {
    localStorage.setItem(`${LS_PREFIX}${cookieName}`, JSON.stringify(value));
  } catch {
    // localStorage blocked or full
  }
}

function getLocalStorage(key: string): string | undefined {
  try {
    return localStorage.getItem(`${LS_PREFIX}${key}`) ?? undefined;
  } catch {
    return undefined;
  }
}

function setLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(`${LS_PREFIX}${key}`, value);
  } catch {
    // localStorage blocked or full
  }
}
